import React from "react";
import { IntlProvider } from "react-intl";

import {
  DEFAULT_LOCALE,
  getMessages,
  isLocale,
  Locale,
  Messages,
  SUPPORTED_LOCALES,
} from "./enhancedIntlModel";
import EnhancedIntlContext, { EnhancedIntl } from "./EnhancedIntlContext";

export type EnhancedIntlProviderProps = React.PropsWithChildren<{}>;

/**
 * Provides i18n capabilities to its children.
 *
 * This component will setup the locale and the messages, take care
 * of the locale changes and persist the locale.
 *
 * @param props - The EnhancedIntlProvider component props.
 */
function EnhancedIntlProvider(props: EnhancedIntlProviderProps) {
  const { children } = props;
  const [locale, setLocale] = React.useState<Locale>(DEFAULT_LOCALE);
  const [innerLocale, setInnerLocale] = React.useState<Locale>(locale);
  const [messages, setMessages] = React.useState<Messages>();
  const enhancedIntl: EnhancedIntl = React.useMemo(() => {
    return {
      defaultLocale: DEFAULT_LOCALE,
      supportedLocales: SUPPORTED_LOCALES,
      locale,
      setLocale,
    };
  }, [locale]);

  // Init the locale.
  React.useEffect(() => {
    // If a locale is already defined in the localStorage, use it.
    const localStorageLocale = localStorage.getItem("ir-locale");
    if (isLocale(localStorageLocale)) {
      if (localStorageLocale === DEFAULT_LOCALE) return;
      setLocale(localStorageLocale);
      return;
    }

    // If a locale is preset as an environment variable, use it.
    const processEnvLocale = process.env.REACT_APP_LOCALE;
    if (isLocale(processEnvLocale)) {
      if (processEnvLocale === DEFAULT_LOCALE) return;
      setLocale(processEnvLocale);
      return;
    }
  }, []);

  // Set the messages when the locale changes.
  React.useEffect(() => {
    if (locale === DEFAULT_LOCALE) {
      setMessages(undefined);
      setInnerLocale(locale);
      return;
    }

    getMessages(locale)
      .then((messages) => {
        setMessages(messages);
        setInnerLocale(locale);
      })
      .catch((error) => {
        setLocale(DEFAULT_LOCALE);
        if (process.env.NODE_ENV !== "production") {
          console.error(error);
        }
      });
  }, [locale]);

  // Persist the locale in the localStorage.
  React.useEffect(() => {
    try {
      localStorage.setItem("ir-locale", locale);
    } catch (error) {
      console.error(error);
    }
  }, [locale]);

  return (
    <EnhancedIntlContext.Provider value={enhancedIntl}>
      <IntlProvider
        locale={innerLocale}
        defaultLocale={DEFAULT_LOCALE}
        messages={messages}
      >
        {children}
      </IntlProvider>
    </EnhancedIntlContext.Provider>
  );
}

export default EnhancedIntlProvider;
